import { ModalComponent } from 'src/scripts/components/modal/modal'
import { Nullable } from 'src/scripts/types/common'
import { isMobile } from 'src/scripts/utils/check-media'
import { isEscKey } from 'src/scripts/utils/key'
import { CarouselComponent } from 'src/scripts/components/carousel/carousel'
import { $el, $list } from 'src/scripts/utils/utils'

const CAROUSEL_SELECTOR = `[data-product-modal-carousel]`
const CAROUSEL_BUTTONS_SELECTOR = `[data-product-modal-carousel-button]`
const CAROUSEL_SLIDE_SELECTOR = `[data-product-modal-slide]`
const CAROUSEL_SELECTED_SLIDE_NUMBER_SELECTOR = `[data-product-modal-selected-slide-number]`
const ZOOM_CURSOR_SELECTOR = `zoom-cursor`

interface ZoomCursorContract extends HTMLElement {
  updateState(isZoomed: boolean): void
}

export class ProductModal extends ModalComponent {
  carousel: Nullable<CarouselComponent>
  mediaIndexMap: any
  isZoomed: boolean = false
  zoomedImage: any
  opener: Nullable<HTMLElement>

  handleKeydown = (event: KeyboardEvent): void => {
    if (isEscKey(event)) {
      event.preventDefault()
      this.isZoomed ? this.zoomedImage?.reset() : void this.hide()
    }
  }

  handleCarouselInit = (): void => {
    this.updateCarouselDraggableState()
  }

  handleCarouselSelect = (): void => {
    this.updateSelectedSlideNumber()
  }

  async openAndShowMediaById(mediaId, opener?: HTMLElement): Promise<void> {
    // 只保存本次打开该 PDP 媒体弹窗的触发器，关闭后恢复至此处。
    this.opener = opener ?? null
    await this.show()

    if (!this.carousel) return

    const mediaIndex = this.mediaIndexMap[mediaId]
    this.carousel.embla.scrollTo(mediaIndex, true)

    const carouselButtons = $list(CAROUSEL_BUTTONS_SELECTOR, this.carousel)
    if (carouselButtons[0]) carouselButtons[0].focus()

    this.updateSelectedSlideNumber()
  }

  initCarousel(): void {
    this.mediaIndexMap = this.createMediaIndexMap()
    if (this.element) this.carousel = $el(CAROUSEL_SELECTOR, this.element)
    this.carousel?.embla.on('select', this.handleCarouselSelect)
    this.carousel?.embla.on('init', this.handleCarouselInit)
  }

  resetCarousel(): void {
    this.carousel?.embla.off('select', this.handleCarouselSelect)
    this.carousel?.embla.off('init', this.handleCarouselInit)
  }

  async hide(): Promise<void> {
    this.resetCarousel()
    this.isZoomed = false
    this.zoomedImage = null
    this.updateModal()
    await super.hide()
    this.restoreOpenerFocus()
  }

  async show(): Promise<void> {
    await super.show()
    this.initCarousel()
  }

  restoreOpenerFocus(): void {
    const opener = this.opener
    this.opener = null

    if (!opener?.isConnected) return

    requestAnimationFrame(() => opener.focus({ preventScroll: true }))
  }

  updateSelectedSlideNumber() {
    const selectedSlideNumber = $el(
      CAROUSEL_SELECTED_SLIDE_NUMBER_SELECTOR,
      this.element,
    )
    if (!selectedSlideNumber || !this.carousel) return

    selectedSlideNumber.textContent = String(
      this.carousel.embla.selectedScrollSnap() + 1,
    )
  }

  updateModal(): void {
    this.updateCarouselDraggableState()
    this.updateZoomCursorState()
    this.updateZoomState()
  }

  createMediaIndexMap() {
    const slides = $list(CAROUSEL_SLIDE_SELECTOR, this.element)
    return slides.reduce((acc, slide, index) => {
      if (!slide.dataset.mediaId) return acc
      return { ...acc, [slide.dataset.mediaId]: index }
    }, {})
  }

  updateCarouselDraggableState() {
    if (!this.carousel) return

    if (!isMobile()) {
      this.carousel.reInit({ watchDrag: false })
      return
    }
    this.carousel.reInit({ watchDrag: !this.isZoomed })
  }

  updateZoomCursorState(): void {
    const zoomCursor = $el(ZOOM_CURSOR_SELECTOR) as ZoomCursorContract
    zoomCursor?.updateState(this.isZoomed)
  }

  updateZoomState(): void {
    this.toggleAttribute('zoom-enabled', this.isZoomed)
  }
}
